import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import {
  getAllSensors,
  getSensors,
} from "../../pages/Events/unitEventsUtils";
import {
  isPropertyGranted,
  isSensorReachable,
  toggleAllProperties,
  togglePropertyWith,
  toggleSensor,
  type GrantDraft,
} from "../../services/grantDraft";
import Spinner from "./Spinner";

/**
 * Picks the properties and sensors one person may reach.
 *
 * Controlled and pure, like UserConfigEditor: it owns no draft of its own and
 * emits every change through `onChange`, so the page that saves holds the only
 * copy. Every rule it applies comes from `services/grantDraft`, which is the one
 * place the precedence rule is written on this side.
 *
 * The shape follows the decision behind the whole feature: **properties are the
 * unit, individual sensors are exceptions.** A grant keyed on bare serials follows
 * a device through a re-home, which is the bug class the location cutoff exists to
 * fix — so ticking a property is the normal act, and reaching inside one is the
 * deliberate exception.
 */
export type PickerProperty = {
  buildingId: string;
  name: string;
};

/** One row of the tenant-wide roster. The serial is `name`; see SingleSensorPicker. */
type RosterRow = { name: string; sensorGroups?: string[] };

export default function ScopePicker({
  value,
  onChange,
  properties,
  disabled = false,
}: {
  value: GrantDraft;
  onChange: (next: GrantDraft) => void;
  properties: PickerProperty[];
  disabled?: boolean;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);

  /**
   * How many sensors sit at each property, for the collapsed rows.
   *
   * ONE request for the whole roster rather than one per property: the per-
   * property fetch below stays lazy, so a fifty-property organization pays one
   * request to show the counts instead of fifty.
   *
   * Counted from the SENSORS' own `sensorGroups`, not from each property's
   * `sensors` list, and the two disagree. The property row over-reports: a
   * sensor that moves away updates its own membership but the old property
   * keeps the stale serial. In the live table 4 properties list serials no
   * sensor names back -- one of them 7 of them -- so counting the property's
   * list would show them as occupied when they hold nothing.
   *
   * Shares the query key with SingleSensorPicker, so opening either warms the
   * other. The whole response is cached, matching every other consumer of a
   * sensors key; unwrapping here is what previously put two shapes under one
   * key and crashed a page.
   */
  const roster = useQuery({
    queryKey: ["all-sensors"],
    queryFn: async () => await getAllSensors(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const rosterRows: RosterRow[] = Array.isArray(roster.data)
    ? (roster.data as RosterRow[])
    : Array.isArray((roster.data as { data?: unknown })?.data)
      ? (roster.data as { data: RosterRow[] }).data
      : [];

  /**
   * Which serials sit at each property, from the roster.
   *
   * Absent while the roster is unknown, so a row says nothing rather than
   * claiming a property is empty on the strength of a request that has not
   * answered. "No sensors yet" and "we do not know yet" are different
   * statements and only one of them is safe to make here.
   *
   * **Every group a sensor names, not just its first.** `SensorDto.SensorGroups`
   * is a list and membership is additive -- `POST sensor-groups/{id}/sensors`
   * adds one without removing the others -- so a sensor genuinely sits at more
   * than one property, which `grantDraft` says out loud. Reading `[0]` alone
   * counted such a sensor at one property and left the others reading "No sensors
   * yet" while a sensor named them: a false statement about access, on the row an
   * admin ticks.
   */
  const serialsByProperty = roster.isSuccess
    ? rosterRows.reduce<Map<string, string[]>>((acc, row) => {
        for (const id of row.sensorGroups ?? []) {
          if (!id) continue;
          acc.set(id, [...(acc.get(id) ?? []), row.name].filter(Boolean));
        }
        return acc;
      }, new Map())
    : null;

  return (
    <div className="space-y-3">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 accent-[#BBDA62]"
          checked={value.allProperties}
          disabled={disabled}
          onChange={() => onChange(toggleAllProperties(value))}
        />
        <span>
          <span className="font-medium">Every property</span>
          <span className="block text-ink-2 text-sm">
            {/* Saying this out loud, because it is the difference between this and
                ticking every property individually -- and the reason an
                organization-wide manager wants it. */}
            Including properties added later. Choosing this clears any individual
            selections.
          </span>
        </span>
      </label>

      {!value.allProperties && (
        <div className="border-t border-black/10 pt-3 space-y-1">
          {properties.length === 0 ? (
            <p className="text-ink-2 text-sm">
              This organization has no properties yet.
            </p>
          ) : (
            properties.map((property) => (
              <PropertyRow
                key={property.buildingId}
                property={property}
                value={value}
                onChange={onChange}
                disabled={disabled}
                expanded={expanded === property.buildingId}
                onToggleExpanded={() =>
                  setExpanded(
                    expanded === property.buildingId ? null : property.buildingId
                  )
                }
                serials={
                  serialsByProperty
                    ? (serialsByProperty.get(property.buildingId) ?? [])
                    : null
                }
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function PropertyRow({
  property,
  value,
  onChange,
  disabled,
  expanded,
  onToggleExpanded,
  serials,
}: {
  property: PickerProperty;
  value: GrantDraft;
  onChange: (next: GrantDraft) => void;
  disabled: boolean;
  expanded: boolean;
  onToggleExpanded: () => void;
  /**
   * This property's serials from the roster, or null while it is still unknown.
   *
   * **From the roster and not from the lazy fetch below**, because everything this
   * row decides is needed whether or not it has been opened. Reading the serials
   * out of the per-property query meant they were `[]` on every collapsed row, and
   * three things quietly followed from that: unticking a property dropped none of
   * its exclusions (the surprise `togglePropertyWith` documents preventing, on the
   * one path that does not require opening the row), ticking it absorbed none of
   * its per-sensor includes, and the "N withheld" / "N added" markers -- whose
   * whole purpose is to show an exception WITHOUT opening the row -- could never
   * appear on a closed one.
   */
  serials: string[] | null;
}) {
  const granted = isPropertyGranted(value, property.buildingId);

  /**
   * The property's sensors, fetched only when it is opened.
   *
   * Lazily, because an organization with fifty properties should not pay fifty
   * requests to render a list of checkboxes nobody has opened. `enabled` keys on
   * `expanded`, and the result is cached under the same key the insights pages use,
   * so opening a property here warms that cache rather than competing with it.
   */
  const sensors = useQuery({
    queryKey: ["sensors", property.buildingId],
    // Stores the WHOLE response, because the three insights consumers of this very
    // key do (useBuildingAggregates, useBuildingReport, SensorDetailPage) and they
    // unwrap at the point of use. An earlier version unwrapped here instead, which
    // put two different shapes under one key: whichever query resolved first decided
    // what the cache held, so opening a property after the insights pages had warmed
    // it handed this component an axios response where it expected an array, and the
    // page crashed. Sharing a cache key means sharing a shape -- the comment above
    // called the sharing a benefit while the shapes disagreed, which is what made it
    // a landmine rather than an optimisation.
    queryFn: async () => await getSensors(property.buildingId),
    enabled: expanded,
    staleTime: 10 * 60_000,
    retry: false,
  });

  // Defensive about the shape as well as aligned on it. A cache entry written by an
  // older bundle, or by a consumer that changes later, must not be able to crash a
  // page again -- an unreadable list of sensors is a degraded property row, not a
  // reason for the admin section to go blank.
  const sensorList = Array.isArray(sensors.data)
    ? (sensors.data as { name: string; displayName?: string }[])
    : Array.isArray((sensors.data as { data?: unknown })?.data)
      ? ((sensors.data as { data: { name: string; displayName?: string }[] }).data)
      : [];

  const known = serials ?? [];

  /**
   * The checkboxes to render: the roster's membership, named by the property's own
   * list where it can name them.
   *
   * The two lists disagree in one direction -- a sensor that moves away updates its
   * own membership while the old property keeps the stale serial -- so rendering the
   * property's list directly offered checkboxes for sensors that are not there, on
   * the same row the count had just marked empty. Taking the membership from the
   * roster and only the DISPLAY NAME from the property's list keeps the row
   * internally consistent: what is counted, what is marked, what is toggled and what
   * is listed are now one set.
   */
  const displayNames = new Map(sensorList.map((s) => [s.name, s.displayName]));
  const checkboxes = known.map((name) => ({
    name,
    displayName: displayNames.get(name),
  }));

  /**
   * How many of this property's sensors are withheld. Shown on the row so an
   * exception is visible without opening it -- otherwise an admin would have to
   * expand every property to find out where the exceptions are.
   */
  const withheld = known.filter((s) => value.excludeSensors.includes(s)).length;
  const addedHere = known.filter((s) => value.includeSensors.includes(s)).length;

  return (
    <div>
      <div className="flex items-center gap-3 py-1.5">
        <input
          type="checkbox"
          id={`property-${property.buildingId}`}
          className="h-4 w-4 accent-[#BBDA62]"
          checked={granted}
          disabled={disabled}
          onChange={() =>
            onChange(togglePropertyWith(value, property.buildingId, known))
          }
        />
        <label
          htmlFor={`property-${property.buildingId}`}
          className="cursor-pointer flex-1"
        >
          {property.name}
        </label>

        {/* An empty property is still worth granting -- a grant on it covers
            whatever is installed there later, which is the reason grants are
            property-level rather than per-serial. So this says so rather than
            hiding the row: removing it would make a forward-looking grant
            unexpressible, and leaving it unmarked is what made an admin pick a
            property and find nothing under it. */}
        {serials !== null && serials.length === 0 && (
          <span className="text-ink-2 text-sm">No sensors yet</span>
        )}

        {withheld > 0 && (
          <span className="text-alert-warning text-sm">
            {withheld} withheld
          </span>
        )}
        {addedHere > 0 && (
          <span className="text-alert-info text-sm">{addedHere} added</span>
        )}

        <button
          type="button"
          onClick={onToggleExpanded}
          aria-expanded={expanded}
          className="text-primary text-sm hover:underline"
        >
          {expanded ? "Hide sensors" : "Choose sensors"}
        </button>
      </div>

      {expanded && (
        <div className="ml-7 mb-2 pl-3 border-l border-black/10">
          {/* The roster decides membership, so "we do not know yet" is ITS pending
              state and not the name fetch's. Keyed on the name fetch instead, an
              expanded row claimed "no sensors at this property yet" for as long as
              the roster was outstanding -- the same false statement the count is
              careful not to make on the collapsed row. The name fetch failing is a
              degradation, not an emptiness: the serials are still the answer. */}
          {serials === null ? (
            <div className="py-2">
              <Spinner size={24} />
            </div>
          ) : checkboxes.length === 0 ? (
            <p className="text-ink-2 text-sm py-2">
              No sensors at this property yet.
            </p>
          ) : (
            checkboxes.map((sensor) => {
              const reachable = isSensorReachable(value, sensor.name, granted);
              return (
                <label
                  key={sensor.name}
                  className="flex items-center gap-3 py-1 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-[#BBDA62]"
                    checked={reachable}
                    disabled={disabled}
                    onChange={() =>
                      onChange(toggleSensor(value, sensor.name, granted))
                    }
                  />
                  <span className="text-sm">
                    {sensor.displayName || sensor.name}
                  </span>
                  {/* Why this sensor is on or off, which is not obvious from a
                      tick alone once properties and exceptions interact. */}
                  {granted && !reachable && (
                    <span className="text-alert-warning text-xs">withheld</span>
                  )}
                  {!granted && reachable && (
                    <span className="text-alert-info text-xs">added</span>
                  )}
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
